import './global.css';

import { useReactNavigationDevTools } from '@dev-plugins/react-navigation';
import { ActionSheetProvider } from '@expo/react-native-action-sheet';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import * as Application from 'expo-application';
import * as Device from 'expo-device';
import { useFonts } from 'expo-font';
import * as Notifications from 'expo-notifications';
import {
  SplashScreen,
  Stack,
  useNavigationContainerRef,
  usePathname,
  useRouter,
  useSegments,
} from 'expo-router';
import {
  AppState,
  BackHandler,
  Linking,
  Platform,
  Pressable,
  StatusBar,
  View,
} from 'react-native';
import appsFlyer from 'react-native-appsflyer';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import Toast from 'react-native-toast-message';

import { APIProvider } from '@/api';
import BackButton from '@/components/back-button';
import { hydrateAuth, isDesktop, useAuth } from '@/core';
import { initSentry } from '@/core/clients/sentry';

export { ErrorBoundary } from 'expo-router';

// Import global CSS file
import '../../global.css';
import '@/style/unistyles';
import '@/core/clients/meta';

import { Env } from '@env';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Constants from 'expo-constants';
import React, { useEffect } from 'react';
import { UnistylesRuntime, useStyles } from 'react-native-unistyles';

import { useCustomer } from '@/api/customers/use-customer';
import { useCreateDevice } from '@/api/devices/use-create-device';
import { AllowPushNotificationsModal } from '@/components/allow-push-notifications-modal';
import { LoginModal } from '@/components/login-modal';
import { useModalStore } from '@/components/login-modal/store';
import Navbar from '@/components/navbar';
import PartnerSidebar from '@/components/partner-sidebar';
import { initStripe } from '@/components/stripe';
import mixpanelService from '@/core/clients/mixpanel';
import { useAttribution } from '@/core/hooks/use-attribution';
import { useOrganizationFavicon } from '@/core/hooks/use-organization-favicon';
import { useOrganizationKeywords } from '@/core/hooks/use-organization-keywords';
import { useScrollbarTheme } from '@/core/hooks/use-scrollbar-theme';
import { useOrganizationBootstrap } from '@/core/organization';
import { useAttributionStore } from '@/stores/use-attribution-store';
import useSettingsStore from '@/stores/use-settings-store';
import { Text, toastConfig } from '@/ui';
import { Icon } from '@/ui/icon';
import { Cancel } from '@/ui/icons/cancel';

export const unstable_settings = {
  initialRouteName: 'index',
};

let dismissedPushPermissionModal = false;

hydrateAuth();
// loadSelectedTheme();
// Prevent the splash screen from auto-hiding before asset loading is complete.
SplashScreen.preventAutoHideAsync();

initSentry();

// temporary polyfill for old libraries
if (!BackHandler.removeEventListener) {
  BackHandler.removeEventListener = (type, handler) => {
    const subscription = BackHandler.addEventListener(type, handler);
    subscription.remove();
  };
}

export default function RootLayout() {
  const navigationRef = useNavigationContainerRef();
  useReactNavigationDevTools(navigationRef);
  return (
    <APIProvider>
      <RootLayoutNav navigationRef={navigationRef} />
    </APIProvider>
  );
}

function RootLayoutNav({
  navigationRef,
}: {
  navigationRef: ReturnType<typeof useNavigationContainerRef>;
}) {
  const { theme } = useStyles();
  const currentRoute = usePathname();
  const isNotFound = useSegments().includes('+not-found');
  const router = useRouter();
  const status = useAuth.use.status();
  const pathname = usePathname();
  const [showAllowPushNotificationsModal, setShowAllowPushNotificationsModal] =
    React.useState(false);
  const askedAndroidPushNoticationsPermission = useSettingsStore(
    (state) => state.askedAndroidPushNoticationsPermission
  );
  const setAskedAndroidPushNoticationsPermission = useSettingsStore(
    (state) => state.setAskedAndroidPushNoticationsPermission
  );
  const fleetInsuranceAnnouncementDismissed = useSettingsStore(
    (state) => state.fleetInsuranceAnnouncementDismissed
  );
  const setFleetInsuranceAnnouncementDismissed = useSettingsStore(
    (state) => state.setFleetInsuranceAnnouncementDismissed
  );
  const loginModalMode = useModalStore((state) => state.loginModalMode);

  useAttribution();
  const { setAttribution } = useAttributionStore();
  useOrganizationBootstrap();
  useScrollbarTheme();
  useOrganizationFavicon();
  useOrganizationKeywords();

  const { mutateAsync: createDevice, isPending: isCreatingDevice } =
    useCreateDevice();

  const { data: customer, refetch: refetchCustomer } = useCustomer({
    enabled: false,
  });

  React.useEffect(() => {
    if (Platform.OS === 'web') return;

    // AppsFlyer deep link handler. Only fires when AppsFlyer has a real deep
    // link to deliver (direct OneLink tap or deferred install attribution).
    // On a normal launch it doesn't fire — the default `/search` redirect in
    // index.tsx handles that case.
    appsFlyer.onDeepLink(async (res) => {
      if (res?.deepLinkStatus !== 'FOUND') return;

      // Ignore non-AppsFlyer URLs that get piped through (push notifications,
      // custom schemes, etc.) — those are handled by Linking.addEventListener.
      if (
        res.data?.link &&
        !res.data.link.includes('appeon.onelink.me') &&
        !res.data.link.includes('go.eonrides.com')
      ) {
        return;
      }

      const pid = res.data.media_source ?? '';
      const c = res.data.campaign ?? '';

      const afParams: Record<string, string> = {};
      for (const [key, value] of Object.entries(res.data)) {
        if (key.toLowerCase().startsWith('af_') && value != null) {
          afParams[key] = String(value);
        }
      }

      // af_sub4 carries website A's (tesla-rentz) anonymous Mixpanel device id.
      // Override this device's persisted identity to match it BEFORE tracking,
      // so events merge into A's anonymous profile under Mixpanel's simplified
      // ID merge. Awaited so Campaign Opened below carries the stitched id.
      // (Do NOT identify() here — that would treat A's anonymous device id as a
      // $user_id and fail to merge with A's anonymous events.)
      const mpId = res.data.af_sub4;
      if (mpId) {
        await mixpanelService.stitchDeviceId(String(mpId));
      }

      setAttribution(pid, c);

      mixpanelService.track('Campaign Opened', {
        Pid: pid,
        C: c,
        ...afParams,
      });

      const {
        pathname: pn,
        deep_link_value: deepLinkValue,
        ...query
      } = res.data;

      let routerOptions = {
        pathname: pn ? `/${pn}` : '/',
        params: query,
      };

      if (pn === 'trips' && query.trip_id && query.screen) {
        const { trip_id, screen, ...rest } = query;
        routerOptions = {
          pathname: `/trips/${trip_id}/${screen}`,
          params: rest,
        };
      }

      if (pn === 'account' && query.screen) {
        const { screen, ...rest } = query;
        if (screen === 'trip-history' && rest.trip_id) {
          routerOptions = {
            pathname: `/account/trip-history/${rest.trip_id}`,
            params: rest,
          };
        } else {
          routerOptions = {
            pathname: `/account/${screen}`,
            params: rest,
          };
        }
      }

      if (deepLinkValue) {
        const parsedVals = JSON.parse(deepLinkValue);
        const { pathname: deepLinkPn, ...deepLinkQuery } = parsedVals;
        if (deepLinkPn) {
          routerOptions = {
            pathname: deepLinkPn.startsWith('/')
              ? deepLinkPn
              : `/${deepLinkPn}`,
            params: { ...query, ...deepLinkQuery },
          };
        }
      }

      router.replace(routerOptions);
    });

    appsFlyer.initSdk(
      {
        devKey: Env.APPSFLYER_DEV_KEY,
        isDebug: Env.APP_ENV === 'development',
        appId: Env.APPLE_APP_ID,
        onInstallConversionDataListener: false,
        onDeepLinkListener: true,
        timeToWaitForATTUserAuthorization: 10,
      },
      (result) => console.log(result),
      (error) => console.error(error)
    );
  }, []);

  React.useEffect(() => {
    if (Platform.OS === 'web') {
      const urlParams = new URLSearchParams(window.location.search);

      // mp_id carries the anonymous device id from website A (tesla-rentz).
      // Override this instance's device id instead of identifying — under
      // Mixpanel's simplified ID merge, events sharing a $device_id resolve to
      // a single profile, stitching the cross-domain anonymous identity. Must
      // run before any track() below so events carry the correct device id.
      const mpId = urlParams.get('mp_id');
      if (mpId) {
        mixpanelService.stitchDeviceId(mpId);
      }

      const pid = urlParams.get('pid') ?? '';
      const c = urlParams.get('c') ?? '';

      if (pid || c) {
        const afParams: Record<string, string> = {};
        urlParams.forEach((value, key) => {
          if (key.toLowerCase().startsWith('af_')) {
            afParams[key] = value;
          }
        });

        setAttribution(pid, c);

        mixpanelService.track('Campaign Opened', {
          Pid: pid,
          C: c,
          ...afParams,
        });
      }

      const route = urlParams.get('route');

      if (route) {
        setTimeout(() => {
          // Split route into pathname and query string
          const [pn, queryString] = route.split('?');
          const query = Object.fromEntries(
            new URLSearchParams(queryString || '')
          );

          router.replace({
            pathname: `/${pn}`,
            params: query,
          });
        }, 100);

        return;
      }

      const deepLinkValue = urlParams.get('deep_link_value');

      if (deepLinkValue) {
        const parsedVals = JSON.parse(deepLinkValue);

        const { pathname: deepLinkPn, ...deepLinkQuery } = parsedVals;

        if (deepLinkPn) {
          const query: Record<string, string> = {};

          urlParams.forEach((value, key) => {
            if (key !== 'deep_link_value') {
              query[key] = value;
            }
          });

          const routerOptions = {
            pathname: deepLinkPn.startsWith('/')
              ? deepLinkPn
              : `/${deepLinkPn}`,
            params: { ...query, ...deepLinkQuery },
          };

          setTimeout(() => {
            router.replace(routerOptions);
          }, 100);
          return;
        }
      }

      const pn = urlParams.get('pathname');

      if (!pn) return;

      const query: Record<string, string> = {};

      urlParams.forEach((value, key) => {
        if (key !== 'pathname') {
          query[key] = value;
        }
      });

      let routerOptions = {
        pathname: pn.startsWith('/') ? pn : `/${pn}`,
        params: query,
      };

      if (pn === 'trips' && query.trip_id && query.screen) {
        const { trip_id, screen, ...rest } = query;

        routerOptions = {
          pathname: `/trips/${trip_id}/${screen}`,
          params: rest,
        };
      }

      if (pn === 'account' && query.screen) {
        const { screen, ...rest } = query;

        if (screen === 'trip-history' && rest.trip_id) {
          routerOptions = {
            pathname: `/account/trip-history/${rest.trip_id}`,
            params: rest,
          };
        } else {
          routerOptions = {
            pathname: `/account/${screen}`,
            params: rest,
          };
        }
      }

      setTimeout(() => {
        router.replace(routerOptions);
      }, 100);
    }
  }, [router]);

  React.useEffect(() => {
    initStripe({
      publishableKey: Env.STRIPE_PUBLISHABLE_KEY,
      merchantIdentifier: 'merchant.com.eon',
      // urlScheme: 'your-url-scheme',
    });
  }, []);

  React.useEffect(() => {
    const handleDeepLink = (event: { url: string }) => {
      const url = event.url;

      if (!url.includes('eon://')) {
        return;
      }

      router.navigate(url.replace('eon://', '/'));
    };

    const subscription = Linking.addEventListener('url', handleDeepLink);
    return () => subscription.remove();
  }, [router]);

  React.useEffect(() => {
    if (customer) {
      mixpanelService.identify(customer.id.toString());
      mixpanelService.initSessionReplay(customer.email);

      mixpanelService.setPeople({
        $name: `${customer.firstname} ${customer.lastname}`,
        $email: customer.email,
        $phone: customer.phone,
      });
    }
  }, [customer]);

  useEffect(() => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      Notifications.setNotificationHandler({
        handleNotification: async () => ({
          shouldShowAlert: true, // Show system banner
          shouldPlaySound: true, // Play notification sound
          shouldSetBadge: false, // Optionally update the app badge
        }),
      });
    }
  }, []);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        Notifications.setBadgeCountAsync(0).catch(console.error);
      }
    });

    return () => subscription.remove();
  }, []);

  useEffect(() => {
    if (status === 'signIn') {
      registerForPushNotificationsAsync()
        .then(async (token) => {
          if (!token) return;

          const res = await refetchCustomer();

          await createDevice({
            pushToken: token,
            platform: Platform.OS,
            appVersion: Application.nativeApplicationVersion!,
            customerId: res.data.id,
          });
        })
        .catch((error: any) => console.error(error));
    }
  }, [status]);

  useEffect(() => {
    registerForPushNotificationsAsync()
      .then(async (token) => {
        if (!token) return;

        await createDevice({
          pushToken: token,
          platform: Platform.OS,
          appVersion: Application.nativeApplicationVersion!,
        });
      })
      .catch((error: any) => console.error(error));
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web') return;

    function redirect(notification: Notifications.Notification) {
      const url = notification.request.content.data?.url;

      if (typeof url === 'string') {
        router.push(url);
      }
    }

    const response = Notifications.getLastNotificationResponse();
    if (response?.notification) {
      redirect(response.notification);
    }

    const subscription = Notifications.addNotificationResponseReceivedListener(
      (response) => {
        redirect(response.notification);
      }
    );

    return () => {
      subscription.remove();
    };
  }, []);

  React.useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handlePopState = () => {
      router.replace(window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      if (Platform.OS !== 'web') return;

      window.removeEventListener('popstate', handlePopState);
    };
  }, [router]);

  React.useEffect(() => {
    if (Platform.OS === 'web') {
      const script = document.createElement('script');
      script.innerHTML = `
        (function(h,o,t,j,a,r){
            h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
            h._hjSettings={hjid:5156965,hjsv:6};
            a=o.getElementsByTagName('head')[0];
            r=o.createElement('script');r.async=1;
            r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
            a.appendChild(r);
        })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
      `;
      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    }
  }, []);

  React.useEffect(() => {
    if (Platform.OS === 'web') {
      const script = document.createElement('script');
      script.innerHTML = `
        !function(t,e,n,s,a,c,i,o,p){t.AppsFlyerSdkObject=a,t.AF=t.AF||function(){(t.AF.q=t.AF.q||[]).push([Date.now()].concat(Array.prototype.slice.call(arguments)))},t.AF.id=t.AF.id||i,t.AF.plugins={},o=e.createElement(n),p=e.getElementsByTagName(n)[0],o.async=1,o.src="https://websdk.appsflyer.com?"+(c.length>0?"st="+c.split(",").sort().join(",")+"&":"")+(i.length>0?"af_id="+i:""),p.parentNode.insertBefore(o,p)}(window,document,"script",0,"AF","banners",{banners: {key: "${Env.APPSFLYER_WEB_KEY}"}});
        AF('banners', 'showBanner', {bannerContainerQuery: "#smartbanner"});
      `;
      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    }
  }, []);

  React.useEffect(() => {
    if (Platform.OS === 'web') {
      if (['/', '/search'].includes(pathname)) {
        const smartbanner = document.getElementById('smartbanner');

        if (smartbanner) {
          smartbanner.style.display = 'block';
        }
      } else {
        const smartbanner = document.getElementById('smartbanner');

        if (smartbanner) {
          smartbanner.style.display = 'none';
        }
      }
    }
  }, [pathname]);

  React.useEffect(() => {
    if (
      status === 'signOut' &&
      loginModalMode === 'none' &&
      ['/join-partner-program'].includes(pathname)
    ) {
      // Defer to the next tick so the root navigator has finished mounting,
      // otherwise a cold deep-link load throws "Attempted to navigate before
      // mounting the Root Layout component."
      const timer = setTimeout(() => {
        router.replace(
          `/partner/login?returnTo=${encodeURIComponent(pathname)}`
        );
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [status, pathname, loginModalMode, router]);

  const [_loaded, error] = useFonts({
    'DMSans-Bold': require('../../public/fonts/DMSans-Bold.ttf'),
    'DMSans-ExtraLight': require('../../public/fonts/DMSans-ExtraLight.ttf'),
    'DMSans-Light': require('../../public/fonts/DMSans-Light.ttf'),
    'DMSans-Regular': require('../../public/fonts/DMSans-Regular.ttf'),
    'DMSans-SemiBold': require('../../public/fonts/DMSans-SemiBold.ttf'),
    'DMSans-Thin': require('../../public/fonts/DMSans-Thin.ttf'),
  });

  async function registerForPushNotificationsAsync() {
    if (Platform.OS === 'android') {
      Notifications.setNotificationChannelAsync('default', {
        name: 'default',
        importance: Notifications.AndroidImportance.MAX,
        vibrationPattern: [0, 250, 250, 250],
        lightColor: '#FF231F7C',
      });
    }

    if (Device.isDevice) {
      const { status: existingStatus } =
        await Notifications.getPermissionsAsync();

      if (Platform.OS === 'ios') {
        if (existingStatus === 'undetermined') {
          if (dismissedPushPermissionModal) {
            return;
          }
          setShowAllowPushNotificationsModal(true);
          return;
        }

        if (existingStatus !== 'granted') {
          console.log(
            'Permission not granted to get push token, requesting permissions...'
          );
          return;
        }
      } else if (Platform.OS === 'android') {
        // KNOWN LIB BUG: android never returns undetermined for push notif status
        if (
          !askedAndroidPushNoticationsPermission &&
          existingStatus !== 'granted'
        ) {
          if (dismissedPushPermissionModal) {
            return;
          }
          setShowAllowPushNotificationsModal(true);
          return;
        }

        if (
          askedAndroidPushNoticationsPermission &&
          existingStatus !== 'granted'
        ) {
          console.log(
            'Permission not granted to get push token, requesting permissions...'
          );
          return;
        }
      }

      const projectId =
        Constants?.expoConfig?.extra?.eas?.projectId ??
        Constants?.easConfig?.projectId;

      if (!projectId) {
        console.error('Project ID not found');
      }

      try {
        const pushTokenString = (
          await Notifications.getExpoPushTokenAsync({
            projectId,
          })
        ).data;

        console.log(pushTokenString);

        return pushTokenString;
      } catch (e: unknown) {
        console.error(e.message);
      }
    } else {
      console.error('Must use physical device for push notifications');
    }
  }

  if (error) {
    console.error(error);
  }

  return (
    <Providers>
      <View style={{ flex: 1 }}>
        <View style={{ display: 'none' }} id="smartbanner" />

        {['md', 'lg'].includes(UnistylesRuntime.breakpoint) &&
        !isNotFound &&
        !['/search'].includes(currentRoute) &&
        !currentRoute.includes('/partner') &&
        !currentRoute.includes('/get-the-app') &&
        !currentRoute.includes('/login') ? (
          <Navbar />
        ) : null}

        <StatusBar backgroundColor={theme.colors.background_2} />

        {isDesktop() &&
        !fleetInsuranceAnnouncementDismissed &&
        !isNotFound &&
        !currentRoute.includes('/partner/login') &&
        (currentRoute.includes('/partner') ||
          currentRoute.includes('/get-the-app')) ? (
          <Pressable
            onPress={() => {
              window.open(
                'https://www.eonrides.com/partners/fleet-insurance',
                '_blank'
              );
            }}
            style={{
              width: '100%',
              height: 30,
              backgroundColor: theme.colors.cardSwitch.active.backgroundColor,
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'row',
              paddingHorizontal: 16,
            }}
          >
            <View
              style={{ flexDirection: 'row', gap: 4, alignItems: 'center' }}
            >
              <Text
                type="caption1"
                style={{ color: theme.colors.text.inverse }}
              >
                New: off-rental insurance from $37/mo, covers your fleet on
                every platform you list on.
              </Text>
              <Text
                type="caption1Bold"
                style={{ color: theme.colors.text.inverse }}
              >
                Learn more
              </Text>
            </View>

            <Pressable
              onPress={(e) => {
                e.stopPropagation();
                setFleetInsuranceAnnouncementDismissed(true);
              }}
              hitSlop={8}
              style={{
                position: 'absolute',
                right: 12,
                top: 0,
                bottom: 0,
                justifyContent: 'center',
                padding: 4,
              }}
            >
              <Icon
                type="naked"
                icon={<Cancel />}
                fillColor={theme.colors.icon.color}
                stroke={theme.colors.icon.color}
                width={10}
                height={10}
              />
            </Pressable>
          </Pressable>
        ) : null}

        <View style={{ flex: 1, flexDirection: 'row' }}>
          {!isNotFound &&
          !currentRoute.includes('/partner/login') &&
          (currentRoute.includes('/partner') ||
            currentRoute.includes('/get-the-app')) ? (
            <PartnerSidebar />
          ) : null}

          <View style={{ flex: 1 }}>
            <Stack
              screenOptions={{
                contentStyle: {
                  backgroundColor: theme.colors.background_1,
                },
              }}
            >
              <Stack.Screen name="index" options={{ headerShown: false }} />
              <Stack.Screen
                name="(app)"
                options={{ headerShown: false, animation: 'none' }}
              />
              <Stack.Screen
                name="login/index"
                options={{ headerShown: false, animation: 'none' }}
              />
              <Stack.Screen
                name="search-details"
                options={{ headerShown: false }}
              />
              <Stack.Screen
                name="confirm-new-password"
                options={{ headerShown: false }}
              />
              <Stack.Screen
                name="rental-terms"
                options={{ headerShown: false }}
              />
              <Stack.Screen name="car/[id]" options={{ headerShown: true }} />
              {Platform.OS === 'web' && isDesktop() ? (
                <Stack.Screen
                  name="trips/[id]/upload-photos/index"
                  options={{ headerShown: false }}
                />
              ) : (
                <Stack.Screen
                  name="trips/[id]/upload-photos/index"
                  options={{
                    headerShown: true,
                    title: '',
                    headerTitle: '',
                    headerTitleAlign: 'center',
                    headerLeft: () => <BackButton />,
                    headerStyle: {
                      backgroundColor: theme.colors.background_1,
                    },
                    headerTintColor: theme.colors.text.primary,
                    headerShadowVisible: false,
                  }}
                />
              )}
              {Platform.OS === 'web' && isDesktop() ? (
                <Stack.Screen
                  name="trips/[id]/cancel/index"
                  options={{ headerShown: false }}
                />
              ) : (
                <Stack.Screen
                  name="trips/[id]/cancel/index"
                  options={{
                    headerShown: true,
                    title: '',
                    headerTitle: '',
                    headerTitleAlign: 'center',
                    headerLeft: () => <BackButton />,
                    headerStyle: {
                      backgroundColor: theme.colors.background_1,
                    },
                    headerTintColor: theme.colors.text.primary,
                    headerShadowVisible: false,
                  }}
                />
              )}
              <Stack.Screen
                name="partner"
                options={{ headerShown: false, animation: 'none' }}
              />
              <Stack.Screen
                name="join-partner-program"
                options={{ headerShown: false, animation: 'none' }}
              />
              <Stack.Screen
                name="get-the-app"
                options={{ headerShown: false, animation: 'none' }}
              />
              <Stack.Screen
                name="+not-found"
                options={{ headerShown: false }}
              />
              {isDesktop() ? (
                <Stack.Screen
                  name="checkout-complete/[id]"
                  options={{ headerShown: false }}
                />
              ) : (
                <Stack.Screen
                  name="checkout-complete/[id]"
                  options={{
                    headerShown: true,
                    title: '',
                    headerTitle: '',
                    headerTitleAlign: 'center',
                    headerLeft: () => null,
                    headerStyle: {
                      backgroundColor: theme.colors.background_1,
                    },
                    headerTintColor: theme.colors.text.primary,
                    headerShadowVisible: false,
                  }}
                />
              )}
            </Stack>
          </View>
        </View>
      </View>

      <LoginModal />
      <AllowPushNotificationsModal
        visible={showAllowPushNotificationsModal}
        onClose={() => {
          dismissedPushPermissionModal = true;
          setShowAllowPushNotificationsModal(false);
        }}
        onAccept={async () => {
          const { status } = await Notifications.requestPermissionsAsync();

          if (status !== 'granted') {
            if (Platform.OS === 'android') {
              setAskedAndroidPushNoticationsPermission(true);
            }
            dismissedPushPermissionModal = true;
            setShowAllowPushNotificationsModal(false);
            console.error('Permission not granted to get push token');
            return;
          }

          const projectId =
            Constants?.expoConfig?.extra?.eas?.projectId ??
            Constants?.easConfig?.projectId;

          if (!projectId) {
            console.error('Project ID not found');
          }

          try {
            const pushTokenString = (
              await Notifications.getExpoPushTokenAsync({
                projectId,
              })
            ).data;

            console.log(pushTokenString);

            return pushTokenString;
          } catch (e: unknown) {
            console.error(e.message);
          } finally {
            setShowAllowPushNotificationsModal(false);
          }
        }}
        onReject={() => {
          dismissedPushPermissionModal = true;
          setShowAllowPushNotificationsModal(false);
          console.error('Permission not granted to get push token');
          return;
        }}
      />
    </Providers>
  );
}

function Providers({ children }: { children: React.ReactNode }) {
  // const stripePromise = loadStripe(Env.STRIPE_PUBLISHABLE_KEY);

  return (
    // <SafeAreaProvider>
    //   <GestureHandlerRootView>
    //     <APIProvider>
    //       <BottomSheetModalProvider>
    //         {children}
    //       </BottomSheetModalProvider>
    //     </APIProvider>
    //   </GestureHandlerRootView>
    // </SafeAreaProvider>

    Platform.OS === 'web' ? (
      <ActionSheetProvider>
        <GoogleOAuthProvider clientId={Env.GOOGLE_WEB_CLIENT_ID}>
          <GestureHandlerRootView>
            <BottomSheetModalProvider>
              {children}
              <Toast config={toastConfig} />
            </BottomSheetModalProvider>
          </GestureHandlerRootView>
        </GoogleOAuthProvider>
      </ActionSheetProvider>
    ) : (
      <ActionSheetProvider>
        <GestureHandlerRootView>
          <BottomSheetModalProvider>
            {children}

            <Toast config={toastConfig} />
          </BottomSheetModalProvider>
        </GestureHandlerRootView>
      </ActionSheetProvider>
    )
  );
}
